import { useCallback } from 'react';
import { isBefore, parseISO, startOfDay } from 'date-fns';
import { useTranslation } from 'next-i18next';
import useDefaultPlaces from 'src/common/components/Search/useDefaultPlaces';
import useCookie from 'src/common/hooks/useCookie';
import recentSearchIdHash from 'src/search/utils/recentSearchIdHash';
import { RetrievedRecentSearch, RecentSearch } from '../types/common/RecentSearch';
import {
  NEAR_ME, RECENT_SEARCHES_KEY, RECENT_SEARCH_COOKIE, SEARCH_INQUIRY_LATEST_KEY,
} from '../constants';
import useLocalStorage from './useLocalStorage';
import { constructRecentSearchCookie } from '../utils/recentInquiryCookieData';

export type SearchInquiryKind = 'inquiry' | 'search';

export type UseRecentSearchesReturn = {
  recentSearches: RetrievedRecentSearch[];
  /**
   * Updates both localStorage and `recentSearches` state.
   * If search already exists, it will reorder to array index 0
   */
  addRecentSearch: (search: RecentSearch) => void;
  addRecentSearchWithPrevious: (search: Partial<RecentSearch>) => void;
  clearRecentSearches: () => void;
};

const MAX_SEARCHES = 10;
/**
 * @WARNING
 * This is a PRIVATE hook, intended for SearchInputProvider only.
 *
 * If you need recentSearches, get them from SearchInputContext:
 * `const { recentSearches, addRecentSearch } = useSearchInputProvider();`
 * Otherwise, `recentSearches` state will be stale.
 *
 * @BackwardsCompatibility
 * If changes are made after prod deploy, add a `version` key to `RecentSearch` type,
 * and use it to delete any non-matching version entry on load.
 * To prevent client errors using old shapes.
 * */
const useRecentSearches = (): UseRecentSearchesReturn => {
  const { t } = useTranslation('common');

  const { nearMePlace } = useDefaultPlaces();

  const parse = useCallback((value: string) => {
    const existingSearchIds = new Set<string>();
    return JSON.parse(value).map((search: RetrievedRecentSearch) => {
      /** Saved nearme will always have place_id 'near-me', but location can be any lang  */
      const localizedLocation = search.place_id === NEAR_ME
        ? nearMePlace.description // translated
        : search.location;

      const dates = search.dates ?? [];
      if (dates[0] && isBefore(
        parseISO(dates[0]),
        startOfDay(new Date()), // beware constructing dates, ideally only DatePicker should
      )) {
        return {
          ...search,
          dates: [],
          // reconstruct id due to change
          searchId: recentSearchIdHash(search),
          location: localizedLocation,
        };
      }
      return {
        ...search,
        dates: Array.isArray(dates) ? dates : [dates],
        location: localizedLocation,
      };
    }).filter((search: RetrievedRecentSearch) => {
      // dedupe searches by searchId
      if (existingSearchIds.has(search.searchId)) return false;
      existingSearchIds.add(search.searchId);
      return true;
    });
  }, [nearMePlace.description]);

  const [, saveAsLatest] = useLocalStorage<SearchInquiryKind>(SEARCH_INQUIRY_LATEST_KEY, 'search', { save: (v) => v });
  const [recentSearches, setRecentSearches] = useLocalStorage<RetrievedRecentSearch[]>(
    RECENT_SEARCHES_KEY,
    [],
    { parse },
  );

  // For use in getFilteredOwnerBoats
  const [, setCookie] = useCookie(RECENT_SEARCH_COOKIE);

  const addRecentSearch = useCallback(async (search: RecentSearch | RetrievedRecentSearch) => {
    let {
      location,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      place_id,
    } = search;

    place_id ||= NEAR_ME; // default if empty
    const isMissingLocation = !search.location;
    // This is to cater for the case where the location is not available in the search object
    // Eg: Searching Stellenbosch then updating the search in the filter modal.
    if (isMissingLocation) {
      location = recentSearches
        .find((prevSearch) => prevSearch.place_id === search.place_id)?.location || t('search--description--near-me');
    }

    const searchWithLocation: RecentSearch = { ...search, location, place_id };
    const searchId = recentSearchIdHash(searchWithLocation);

    let newSearch: RetrievedRecentSearch = { ...searchWithLocation, searchId };

    const existingSearchIndex = recentSearches.findIndex(
      (prevSearch) => prevSearch.searchId === searchId,
    );

    if (existingSearchIndex >= 0) {
      // if recentSearch exists, move to index 0 (as most recent search)
      [newSearch] = recentSearches.splice(existingSearchIndex, 1);
    }
    const searches = [newSearch, ...recentSearches] as RetrievedRecentSearch[];

    setRecentSearches(searches.slice(0, MAX_SEARCHES));
    saveAsLatest('search');
    setCookie(constructRecentSearchCookie(newSearch), { expires: 1 });
  }, [recentSearches, saveAsLatest, setCookie, setRecentSearches, t]);

  const addRecentSearchWithPrevious = useCallback((
    values: Partial<RecentSearch>,
  ) => {
    if (recentSearches.length > 0) {
      addRecentSearch({
        ...recentSearches[0],
        ...values,
      });
    }
  }, [addRecentSearch, recentSearches]);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
  }, [setRecentSearches]);

  return {
    recentSearches,
    addRecentSearch,
    addRecentSearchWithPrevious,
    clearRecentSearches,
  };
};

export default useRecentSearches;
